/**
 * Utility functions to handle object mutability issues
 * Ensures objects can be modified even when they're frozen, sealed, or have non-configurable properties
 */
import Vec2 from 'vec2';

/**
 * Forces assignment of a property to an object using multiple fallback strategies
 * @param obj The object to assign to
 * @param prop The property name
 * @param value The value to assign
 * @returns true if assignment was successful, false otherwise
 */
export const forceAssignProperty = <T extends object, K extends keyof T>(
  obj: T,
  prop: K,
  value: T[K]
): boolean => {
  try {
    obj[prop] = value;

    return true;
  } catch (error) {
    try {
      Object.defineProperty(obj, prop, {
        value,
        writable: true,
        enumerable: true,
        configurable: true,
      });

      return true;
    } catch (defineError) {
      try {
        delete obj[prop];
        Object.defineProperty(obj, prop, {
          value,
          writable: true,
          enumerable: true,
          configurable: true,
        });

        return true;
      } catch (deleteError) {
        try {
          return Reflect.set(obj, prop, value);
        } catch (reflectError) {
          console.warn(
            `Failed to assign property '${String(prop)}' to object:`,
            error
          );

          return false;
        }
      }
    }
  }
};

/**
 * Safely assigns a value to an array index, handling read-only arrays
 * @param array The array to assign to
 * @param index The index to assign at
 * @param value The value to assign
 * @returns true if assignment was successful, false otherwise
 */
export const forceAssignArrayElement = <T>(
  array: T[],
  index: number,
  value: T
): boolean => {
  try {
    array[index] = value;

    return true;
  } catch (error) {
    try {
      Object.defineProperty(array, index, {
        value,
        writable: true,
        enumerable: true,
        configurable: true,
      });

      return true;
    } catch (defineError) {
      try {
        delete array[index];
        Object.defineProperty(array, index, {
          value,
          writable: true,
          enumerable: true,
          configurable: true,
        });

        return true;
      } catch (deleteError) {
        try {
          return Reflect.set(array, index, value);
        } catch (reflectError) {
          console.warn(
            `Failed to assign array element at index ${index}:`,
            error
          );

          return false;
        }
      }
    }
  }
};

/**
 * Safe replacement for Object.assign that handles read-only properties
 * @param target The target object
 * @param sources The source objects
 * @returns true if all assignments were successful
 */
export const safeAssign = <T extends object>(
  target: T,
  ...sources: Partial<T>[]
): boolean => {
  let allSuccessful = true;

  sources.forEach((source) => {
    if (source) {
      Object.keys(source).forEach((key) => {
        const value = source[key as keyof T];

        if (value !== undefined) {
          const success = forceAssignProperty(target, key as keyof T, value);

          if (!success) {
            allSuccessful = false;
          }
        }
      });
    }
  });

  return allSuccessful;
};

/**
 * Creates a mutable Vec2 object preserving all methods
 * @param x The x coordinate
 * @param y The y coordinate
 * @returns A mutable Vec2 object with all methods intact
 */
const createMutableVec2 = (x: number, y: number): Vec2 => {
  const vec2 = new Vec2(x, y);

  Object.defineProperty(vec2, 'x', {
    value: x,
    writable: true,
    enumerable: true,
    configurable: true,
  });
  Object.defineProperty(vec2, 'y', {
    value: y,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  return vec2;
};

/**
 * Creates a completely mutable copy of an object
 * @param obj The object to copy
 * @returns A mutable copy of the object
 */
export const createMutableCopy = <T extends object>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) =>
      typeof item === 'object' && item !== null ? createMutableCopy(item) : item
    ) as unknown as T;
  }

  if (obj instanceof Vec2) {
    return createMutableVec2(obj.x, obj.y) as unknown as T;
  }

  const copy = {} as T;

  Object.keys(obj).forEach((key) => {
    const value = obj[key as keyof T];

    if (typeof value === 'object' && value !== null) {
      copy[key as keyof T] = createMutableCopy(value);
    } else {
      copy[key as keyof T] = value;
    }
  });

  Object.keys(copy).forEach((key) => {
    const descriptor = Object.getOwnPropertyDescriptor(copy, key);

    if (descriptor && !descriptor.writable) {
      Object.defineProperty(copy, key, {
        ...descriptor,
        writable: true,
        configurable: true,
      });
    }
  });

  return copy;
};

/**
 * Ensures an object is mutable by creating a copy if necessary
 * @param obj The object to ensure is mutable
 * @returns The original object if mutable, or a mutable copy
 */
export const ensureMutable = <T extends object>(obj: T): T => {
  if (Array.isArray(obj)) {
    return ensureMutableArray(obj) as T;
  }

  if (obj instanceof Vec2) {
    return createMutableVec2(obj.x, obj.y) as unknown as T;
  }

  if (Object.isExtensible(obj)) {
    const keys = Object.keys(obj);

    if (keys.length === 0) {
      return obj;
    }

    const firstKey = keys[0] as keyof T;
    const originalValue = obj[firstKey];

    try {
      obj[firstKey] = originalValue;

      return obj;
    } catch (error) {
      return createMutableCopy(obj);
    }
  } else {
    return createMutableCopy(obj);
  }
};

/**
 * Ensures an array is mutable by creating a copy if necessary
 * @param array The array to ensure is mutable
 * @returns The original array if mutable, or a mutable copy
 */
export const ensureMutableArray = <T>(array: T[]): T[] => {
  if (!Array.isArray(array)) {
    return array;
  }

  try {
    if (array.length > 0) {
      const originalValue = array[0];

      array[0] = originalValue;

      return array;
    } else {
      const testValue = {} as T;

      array.push(testValue);
      array.pop();

      return array;
    }
  } catch (error) {
    return [...array];
  }
};

/**
 * Ensures an object is deeply mutable (all nested objects are also mutable)
 * @param obj The object to ensure is deeply mutable
 * @returns The original object if deeply mutable, or a deeply mutable copy
 */
export const ensureDeepMutable = <T extends object>(obj: T): T =>
  createMutableCopy(obj);
